import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import api from '../api';

function Scholarships({ user, onLogout }) {
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    organization: '',
    amount: '',
    currency: 'CAD',
    start_date: '',
    end_date: '',
    status: 'applied',
    description: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadScholarships();
  }, []);

  const loadScholarships = async () => {
    try {
      const response = await api.getScholarships();
      setScholarships(response.data);
    } catch (err) {
      setError('Error loading scholarships');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      organization: '',
      amount: '',
      currency: 'CAD',
      start_date: '',
      end_date: '',
      status: 'applied',
      description: ''
    });
    setEditingId(null);
    setError('');
  };

  const handleAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (scholarship) => {
    setFormData({
      title: scholarship.title || '',
      organization: scholarship.organization || '',
      amount: scholarship.amount || '',
      currency: scholarship.currency || 'CAD',
      start_date: scholarship.start_date ? new Date(scholarship.start_date).toISOString().split('T')[0] : '',
      end_date: scholarship.end_date ? new Date(scholarship.end_date).toISOString().split('T')[0] : '',
      status: scholarship.status || 'applied',
      description: scholarship.description || ''
    });
    setEditingId(scholarship._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this scholarship?')) {
      try {
        setError('');
        console.log('Deleting scholarship with ID:', id);
        const response = await api.deleteScholarship(id);
        console.log('Delete response:', response);
        await loadScholarships();
        alert('Scholarship deleted successfully');
      } catch (err) {
        const errorMsg = err.response?.data?.error || 'Error deleting scholarship';
        setError(errorMsg);
        console.error('Delete scholarship error:', err);
        console.error('Error response:', err.response?.data);
        alert(`Failed to delete scholarship: ${errorMsg}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingId) {
        await api.updateScholarship(editingId, formData);
      } else {
        await api.createScholarship(formData);
      }
      setShowModal(false);
      resetForm();
      loadScholarships();
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving scholarship');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="dashboard-container">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="main-content">
        <div className="page-header">
          <h1>Scholarships</h1>
          <p>Manage scholarship applications and awards</p>
        </div>

        <div className="content-section">
          <div className="section-header">
            <h2>My Scholarships</h2>
            <button className="btn btn-success" onClick={handleAdd}>
              + Add Scholarship
            </button>
          </div>

          {loading ? (
            <div>Loading...</div>
          ) : scholarships.length === 0 ? (
            <div className="empty-state">
              <p>No scholarships yet</p>
              <button className="btn btn-primary" onClick={handleAdd}>
                Add your first scholarship
              </button>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Organization</th>
                    <th>Amount</th>
                    <th>Start Date</th>
                    <th>Status</th>
                    {user.role === 'director' && <th>Researcher</th>}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scholarships.map((scholarship) => (
                    <tr key={scholarship._id}>
                      <td data-label="Title">{scholarship.title}</td>
                      <td data-label="Organization">{scholarship.organization}</td>
                      <td data-label="Amount">
                        {scholarship.amount ? `${scholarship.currency} ${scholarship.amount}` : '-'}
                      </td>
                      <td data-label="Start Date">
                        {scholarship.start_date ? new Date(scholarship.start_date).toLocaleDateString() : '-'}
                      </td>
                      <td data-label="Status">
                        <span className={`status-badge status-${scholarship.status}`}>
                          {scholarship.status}
                        </span>
                      </td>
                      {user.role === 'director' && (
                        <td data-label="Researcher">
                          {scholarship.user_id?.full_name || scholarship.user_id?.username || 'Unknown'}
                        </td>
                      )}
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn btn-primary btn-small"
                            onClick={() => handleEdit(scholarship)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-danger btn-small"
                            onClick={() => handleDelete(scholarship._id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingId ? 'Edit Scholarship' : 'Add Scholarship'}</h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {error && <div className="error-message">{error}</div>}
                  
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="e.g., Graduate Research Scholarship"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Organization *</label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      placeholder="e.g., National Science Foundation"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Amount</label>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      placeholder="e.g., 25000"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Currency</label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                    >
                      <option value="CAD">CAD</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="applied">Applied</option>
                      <option value="awarded">Awarded</option>
                      <option value="rejected">Rejected</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Enter scholarship description or notes..."
                    />
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button type="submit" className="btn btn-primary">
                    {editingId ? 'Update Scholarship' : 'Add Scholarship'}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Scholarships;
